import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Tooltip } from 'antd-v5';
import { MdOutlineAttachment } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Heading } from 'scouting-ui/lib';

import { Button } from '@shared/components';

import {
  ALLOWED_EXTENSIONS,
  MAX_ATTACHMENT_COUNT,
  MAX_EMAIL_SIZE_MB,
  MAX_FILE_SIZE_MB,
  MAX_MBATTACHMENT_COUNT,
  MAX_MBFILE_SIZE_MB,
} from '../../constants';
import styles from './AttachmentAdd.less';
import { hasFiles, validateFiles } from './utils';

interface AttachmentProps {
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  isMultiple?: boolean;
}
export default function AttachmentAdd({
  files,
  setFiles,
  isMultiple = true,
}: AttachmentProps) {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachmentCount = isMultiple
    ? MAX_ATTACHMENT_COUNT
    : MAX_MBATTACHMENT_COUNT;
  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList) return;

      const acceptedFiles = validateFiles(files, fileList, isMultiple);
      if (acceptedFiles.length) {
        setFiles(prevFiles => [...prevFiles, ...acceptedFiles]);
      }
    },
    [files, setFiles, isMultiple],
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLLabelElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(true);
    },
    [],
  );

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
    dragCounterRef.current = 0;
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLLabelElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);
      dragCounterRef.current = 0;
      handleFiles(event.dataTransfer.files);
    },
    [handleFiles],
  );

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(event.target.files);
      event.target.value = '';
    },
    [handleFiles],
  );

  const handleOpenFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const fileLimitMessage = useMemo(() => {
    if (isMultiple) {
      return (
        <FormattedMessage
          id="messaging.attachment.filesLimitation"
          values={{
            maxFileSize: MAX_FILE_SIZE_MB,
            maxTotalSize: MAX_EMAIL_SIZE_MB,
            maxAttachmentCount: MAX_ATTACHMENT_COUNT,
          }}
        />
      );
    } else {
      return (
        <FormattedMessage
          id="messaging.attachment.mbfileLimitation"
          values={{
            maxFileSize: MAX_MBFILE_SIZE_MB,
            maxAttachmentCount: MAX_MBATTACHMENT_COUNT,
          }}
        />
      );
    }
  }, [isMultiple]);

  useEffect(() => {
    const handleWindowDragEnter = (event: DragEvent) => {
      if (!hasFiles(event.dataTransfer)) {
        return;
      }

      event.preventDefault();
      dragCounterRef.current += 1;
      setIsDragging(true);
    };

    const handleWindowDragOver = (event: DragEvent) => {
      if (!hasFiles(event.dataTransfer)) {
        return;
      }

      event.preventDefault();
      setIsDragging(true);
    };

    const handleWindowDragLeave = (event: DragEvent) => {
      if (!hasFiles(event.dataTransfer)) {
        return;
      }

      event.preventDefault();
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);

      if (dragCounterRef.current === 0) {
        setIsDragging(false);
      }
    };

    const handleWindowDrop = (event: DragEvent) => {
      if (!hasFiles(event.dataTransfer)) {
        return;
      }

      const { dataTransfer } = event;

      if (!dataTransfer) {
        return;
      }

      event.preventDefault();
      dragCounterRef.current = 0;
      setIsDragging(false);
      handleFiles(dataTransfer.files);
    };

    window.addEventListener('dragenter', handleWindowDragEnter);
    window.addEventListener('dragover', handleWindowDragOver);
    window.addEventListener('dragleave', handleWindowDragLeave);
    window.addEventListener('drop', handleWindowDrop);

    return () => {
      window.removeEventListener('dragenter', handleWindowDragEnter);
      window.removeEventListener('dragover', handleWindowDragOver);
      window.removeEventListener('dragleave', handleWindowDragLeave);
      window.removeEventListener('drop', handleWindowDrop);
    };
  }, [handleFiles]);

  return (
    <div className={styles.container}>
      <label
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={styles.label}
      >
        <input
          ref={fileInputRef}
          accept={Array.from(ALLOWED_EXTENSIONS)
            .map(ext => `.${ext}`)
            .join(',')}
          multiple={isMultiple}
          hidden
          onChange={handleFileSelect}
          type="file"
        />
      </label>
      <Tooltip title={fileLimitMessage}>
        <Button
          disabled={files.length >= attachmentCount}
          icon={<MdOutlineAttachment />}
          onClick={handleOpenFilePicker}
          shape="round"
          size="large"
        >
          <FormattedMessage id="messaging.attachment.addFiles" />
        </Button>
      </Tooltip>

      {isDragging && (
        <div className={styles.dropzoneActive}>
          <Heading>
            <FormattedMessage id="messaging.attachment.dropFilesHere" />
          </Heading>
          {fileLimitMessage}
        </div>
      )}
    </div>
  );
}
